/*
 * The app frame (DESIGN.md §6.1, ADR 0019, ADR 0028). A light sidebar, a white header with
 * the page title, and the page in a soft blue-grey inset panel that fills the width. Three
 * layouts:
 *   width >= 1024px  248px sidebar (collapsible to a 72px icon rail)
 *   640–1023px       72px rail with Menu; the nav opens as a floating sheet
 *   width < 640px    no rail; Menu sits in the header and the nav opens as a sheet
 */
.shell {
  display: grid;
  grid-template-columns: var(--nav-width) minmax(0, 1fr);
  min-height: 100vh;
  background: var(--sidebar);
}

.skipLink {
  position: absolute;
  top: var(--space-2);
  left: var(--space-2);
  z-index: var(--z-toast);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-control);
  background: var(--brand);
  color: var(--on-brand);
  transform: translateY(-200%);
}

.skipLink:focus-visible {
  outline-color: var(--ink);
  transform: none;
}

/* The sidebar. */
.rail {
  position: sticky;
  top: 0;
  z-index: var(--z-nav);
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding: var(--space-4);
  background: var(--sidebar);
  color: var(--graphite);
}

.brandRow {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  min-height: var(--space-7);
  padding: 0 var(--space-1) var(--space-3) var(--space-2);
  border-bottom: 1px solid var(--rule);
}

.brand {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-width: 0;
  color: var(--ink);
}

/* A plain monogram: the app name's first letter, white on the primary blue. */
.brandMark {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--radius-control);
  background: var(--brand);
  color: var(--on-brand);
  font: var(--t-body-strong);
}

.brandName {
  font: var(--t-heading);
  font-weight: 600;
}

.collapseButton {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: var(--radius-control);
  background: var(--paper);
  color: var(--graphite);
  cursor: pointer;
  transition: background-color var(--dur) var(--ease);
}

.collapseButton[data-hovered] {
  background: var(--brand-tint);
  color: var(--ink);
}

.collapseButton[data-pressed] {
  background: var(--brand-tint-strong);
}

.collapseButton[data-focus-visible] {
  outline: var(--focus-ring);
  outline-offset: var(--focus-offset);
}

.menuButton,
.scrim,
.sheetHead {
  display: none;
}

.sidebarSearch {
  display: flex;
  flex: none;
  margin-top: var(--space-4);
}

.nav {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-5);
  min-height: 0;
  margin: var(--space-4) calc(-1 * var(--space-2)) 0;
  padding: var(--space-1) var(--space-2);
  overflow-y: auto;
}

.navGroup {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.groupLabel {
  padding: 0 var(--space-3);
  color: var(--pencil);
  font: var(--t-small);
}

.navList {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.navFooter {
  margin-top: auto;
}

.navLink {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-height: var(--control-height);
  padding: 0 var(--space-3);
  border-radius: var(--radius-control);
  color: var(--graphite);
  font: var(--t-body);
  text-decoration: none;
  transition:
    background-color var(--dur) var(--ease),
    color var(--dur) var(--ease);
}

.navIcon {
  flex: none;
  color: var(--pencil);
}

.navLabel {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.navLink[data-hovered] {
  background: var(--wash);
  color: var(--ink);
}

.navLink[data-hovered] .navIcon {
  color: var(--graphite);
}

.navLink[data-pressed] {
  background: var(--brand-tint-strong);
}

/* The current page: a primary-tint pill with the icon and label in primary blue, and the
   weight steps up so the place never rests on colour alone (ADR 0028). */
.navLink[aria-current='page'] {
  background: var(--brand-tint);
  color: var(--brand);
  font-weight: 600;
}

.navLink[aria-current='page'] .navIcon {
  color: var(--brand);
}

.navLink[data-focus-visible] {
  outline: var(--focus-ring);
  outline-offset: -2px;
}

/* The white column: header, then the inset content panel. */
.main {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 100vh;
  background: var(--paper);
}

.topBar {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  min-height: calc(var(--topbar-height) + var(--space-4));
  padding: var(--space-3) calc(var(--gutter) + var(--space-2));
  background: var(--paper);
}

.titleSlot {
  flex: 1;
  min-width: 0;
}

.topBarEnd {
  display: flex;
  flex: none;
  align-items: center;
  gap: var(--space-3);
  min-width: 0;
  max-width: 50%;
}

.facility,
.account,
.search {
  display: flex;
  align-items: center;
  min-width: 0;
}

/* A long facility name is cut with an ellipsis rather than pushing the account off screen. */
.facility > strong,
.facility > span {
  min-width: 0;
  overflow: hidden;
  color: var(--graphite);
  font: var(--t-body);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.divider {
  flex: none;
  width: 1px;
  height: var(--space-5);
  background: var(--rule);
}

.statusStrip {
  margin: 0 var(--gutter) var(--space-3);
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-control);
  background: var(--night);
  color: var(--on-brand);
  font: var(--t-body-strong);
}

/* The soft grey inset panel. It fills the width; screens lay cards out inside it. A screen
   that renders its parts straight into the panel gets them stacked 24px apart, the same
   gap screen.page and CardGrid use, so a card never needs a stacking margin of its own. */
.content {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-5);
  min-width: 0;
  margin: 0 var(--gutter) var(--gutter);
  padding: var(--space-5);
  border-radius: var(--radius-panel);
  background: var(--canvas);
  outline: none;
  /* Screens size their card grids to the panel, not the window (CardGrid, home). */
  container-type: inline-size;
}

/*
 * Surfaces (ADR 0019): a section placed directly in a page is a white card in the panel.
 * :where() keeps this at zero specificity, so any screen's own class wins where it sets
 * the same property, and a section can opt out with data-surface="none". Sections inside
 * another section stay flat, so cards never nest.
 */
:where(.content section:not(section section, [data-surface='none'])) {
  padding: var(--card-padding);
  border: var(--card-border);
  border-radius: var(--radius-card);
  background: var(--paper);
  box-shadow: var(--shadow-card);
}

/* Page header: the title, an optional sentence, and the page's actions. */
.pageHeader {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3) var(--space-4);
  margin-bottom: var(--space-5);
}

/* In the shell's header: centred on the header row, with no space under it. */
.pageHeader[data-in-shell] {
  align-items: center;
  margin-bottom: 0;
}

.pageHeading {
  display: flex;
  flex: 1 1 20rem;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.pageTitle {
  color: var(--ink);
  font: var(--t-display);
  overflow-wrap: anywhere;
}

.pageDescription {
  max-width: 75ch;
  color: var(--graphite);
}

.pageHeader[data-in-shell] .pageDescription {
  font: var(--t-small);
}

.pageActions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
}

/* A touch of colour (ADR 0028): the sentence and its one action in a soft primary-tint box. */
.emptyState {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-4);
  padding: var(--space-5);
  border-radius: var(--radius-control);
  background: var(--brand-tint);
  color: var(--graphite);
}

.emptyState p {
  max-width: 75ch;
}

/* Wide screens, sidebar collapsed by the person: an icon rail. Names stay for screen
   readers; the group labels still name their lists. */
@media (width >= 1024px) {
  .shell[data-collapsed] {
    grid-template-columns: var(--nav-width-collapsed) minmax(0, 1fr);
  }

  .shell[data-collapsed] .rail {
    padding: var(--space-4) var(--space-3);
  }

  .shell[data-collapsed] .brandRow {
    flex-direction: column;
    padding: 0 0 var(--space-3);
  }

  .shell[data-collapsed] .brandName,
  .shell[data-collapsed] .groupLabel,
  .shell[data-collapsed] .navLabel {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  .shell[data-collapsed] .nav {
    margin: var(--space-4) calc(-1 * var(--space-1)) 0;
    padding: var(--space-1);
  }

  .shell[data-collapsed] .navLink {
    justify-content: center;
    padding: 0;
  }
}

/* Narrower: the nav folds behind Menu and opens as a sheet. */
@media (width < 1024px) {
  .shell {
    grid-template-columns: var(--nav-width-collapsed) minmax(0, 1fr);
  }

  .rail {
    align-items: center;
    padding: var(--space-3) var(--space-2);
  }

  .brandRow {
    justify-content: center;
    width: 100%;
    padding: 0 0 var(--space-3);
  }

  .brandName,
  .collapseButton {
    display: none;
  }

  .menuButton {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--space-1);
    width: 100%;
    height: 56px;
    margin-top: var(--space-3);
    border: 0;
    border-radius: var(--radius-control);
    background: transparent;
    color: var(--graphite);
    font: var(--t-micro);
    font-weight: 600;
    cursor: pointer;
    transition: background-color var(--dur) var(--ease);
  }

  .menuButton[data-hovered] {
    background: var(--brand-tint);
    color: var(--ink);
  }

  .menuButton[data-pressed] {
    background: var(--brand-tint-strong);
  }

  .menuButton[data-focus-visible] {
    outline: var(--focus-ring);
    outline-offset: -2px;
  }

  /* Three short lines, drawn with borders so no icon font is needed. */
  .menuIcon {
    position: relative;
    width: 18px;
    height: 12px;
    border-top: 2px solid currentcolor;
    border-bottom: 2px solid currentcolor;
  }

  .menuIcon::before {
    position: absolute;
    top: 3px;
    right: 0;
    left: 0;
    border-top: 2px solid currentcolor;
    content: '';
  }

  .nav {
    display: none;
  }

  .scrim {
    position: fixed;
    inset: 0;
    display: block;
    background: var(--scrim);
    animation: fade var(--dur) var(--ease);
  }

  .nav[data-open] {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    display: flex;
    width: min(var(--nav-width) + var(--space-7), calc(100vw - var(--space-7)));
    margin: 0;
    padding: 0 var(--space-3) var(--space-4);
    border-radius: 0 var(--radius-float) var(--radius-float) 0;
    background: var(--sidebar);
    box-shadow: var(--shadow-float);
    animation: fade var(--dur) var(--ease);
  }

  .sheetHead {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: space-between;
    height: var(--topbar-height);
    padding-left: var(--space-3);
    border-bottom: 1px solid var(--rule);
  }

  .sheetBrand {
    color: var(--ink);
    font: var(--t-heading);
    font-weight: 600;
  }

  .closeButton {
    min-height: var(--control-height);
    padding: 0 var(--space-3);
    border: 0;
    border-radius: var(--radius-control);
    background: transparent;
    color: var(--ink);
    font: var(--t-body-strong);
    cursor: pointer;
  }

  .closeButton[data-hovered] {
    background: var(--brand-tint);
  }

  .closeButton[data-pressed] {
    background: var(--brand-tint-strong);
  }

  .closeButton[data-focus-visible] {
    outline: var(--focus-ring);
    outline-offset: -2px;
  }

  .navLink {
    min-height: var(--touch-target);
  }

  .topBarEnd {
    max-width: none;
  }
}

/* Phones: no rail. Menu sits at the left of the header and scrolls away with it. */
@media (width < 640px) {
  .shell {
    grid-template-columns: minmax(0, 1fr);
    background: var(--paper);
  }

  .rail {
    position: absolute;
    top: 0;
    left: 0;
    width: calc(var(--touch-target) + var(--space-3));
    height: auto;
    padding: var(--space-2) 0 0 var(--space-2);
    background: transparent;
  }

  .brandRow {
    display: none;
  }

  .menuButton {
    width: var(--touch-target);
    height: var(--touch-target);
    margin: 0;
    border: 1px solid var(--border);
    background: var(--paper);
  }

  .menuButton[data-hovered] {
    background: var(--brand-tint);
  }

  /* The title takes the row; search and account keep their size. */
  .topBar {
    flex-wrap: wrap;
    gap: var(--space-2) var(--space-3);
    padding: var(--space-2) var(--gutter) var(--space-3) calc(var(--touch-target) + var(--space-4));
  }

  /* Search, facility and account share the row; the facility name gives way first. */
  .topBarEnd {
    flex: 1 1 100%;
    justify-content: flex-end;
    gap: var(--space-2);
  }

  .facility {
    flex: 0 1 auto;
  }

  .divider {
    display: none;
  }

  .statusStrip {
    margin: 0 0 var(--space-2);
    border-radius: 0;
  }

  .content {
    margin: 0;
    padding: var(--space-4) var(--gutter);
    border-radius: 0;
  }

  .pageHeading {
    flex-basis: 100%;
  }

  .pageTitle {
    font: var(--t-title);
  }
}

@keyframes fade {
  from {
    opacity: 0;
  }
}
